import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, ArrowUpRight, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "./ui/sheet";
import { navLinks, studio } from "../mock";
import { cn } from "../lib/utils";

const SECRET_TAPS = 5;
const TAP_WINDOW_MS = 3000;

/** Tapping the "A" 5 times within 3s opens the admin login; single taps behave like the normal home link. */
const useSecretTaps = () => {
  const navigate = useNavigate();
  const taps = useRef(0);
  const timer = useRef(null);
  return () => {
    taps.current += 1;
    clearTimeout(timer.current);
    if (taps.current >= SECRET_TAPS) {
      taps.current = 0;
      navigate("/admin/login");
      return;
    }
    timer.current = setTimeout(() => {
      taps.current = 0;
    }, TAP_WINDOW_MS);
    navigate("/");
  };
};

const Logo = ({ className }) => {
  const onTapA = useSecretTaps();
  return (
    <span className={cn("group flex items-center gap-3", className)} data-testid="nav-logo">
      <button
        type="button"
        onClick={onTapA}
        aria-label="Avalon home"
        className="relative flex h-9 w-9 items-center justify-center border border-foreground/30 transition-colors duration-300 group-hover:border-primary"
        data-testid="nav-logo-mark"
      >
        <span className="display text-2xl leading-none text-foreground transition-colors duration-300 group-hover:text-primary">A</span>
      </button>
      <Link to="/" className="flex flex-col leading-none" data-testid="nav-logo-home">
        <span className="display text-2xl tracking-wide text-foreground">AVALON</span>
        <span className="text-[9px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
          Tattoo Collective
        </span>
      </Link>
    </span>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleHashLink = (e, to) => {
    if (!to.includes("#")) return;
    e.preventDefault();
    const [path, hash] = to.split("#");
    setOpen(false);
    if (location.pathname !== (path || "/")) {
      navigate(`${path || "/"}#${hash}`);
    } else {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${hash}`);
    }
  };

  const isActive = (to) => {
    if (to.includes("#")) return false;
    return location.pathname === to;
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500",
        scrolled
          ? "border-b border-border/70 bg-background/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      )}
      data-testid="navbar"
    >
      <div className="container-x flex h-[76px] items-center justify-between">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={(e) => handleHashLink(e, link.to)}
              className={cn(
                "link-underline text-[12px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300",
                isActive(link.to) ? "text-primary" : "text-foreground/80 hover:text-foreground"
              )}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={studio.phoneHref}
            className="flex items-center gap-2 text-[12px] font-semibold tracking-[0.12em] text-muted-foreground transition-colors duration-300 hover:text-foreground"
            data-testid="nav-phone"
          >
            <Phone className="h-3.5 w-3.5" />
            {studio.phone}
          </a>
          <Button asChild className="rounded-none px-5 font-semibold uppercase tracking-[0.18em] text-[11px]" data-testid="nav-book-cta">
            <Link to="/artists">
              Book an Artist <ArrowUpRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" data-testid="nav-mobile-trigger">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88vw] max-w-sm border-border bg-background p-0">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <SheetDescription className="sr-only">Site links and contact details</SheetDescription>
            <div className="flex h-full flex-col">
              <div className="border-b border-border px-6 py-5">
                <Logo />
              </div>
              <nav className="flex flex-1 flex-col gap-1 px-6 py-8" aria-label="Mobile">
                {navLinks.map((link, i) => (
                  <SheetClose asChild key={link.to}>
                    <NavLink
                      to={link.to}
                      onClick={(e) => handleHashLink(e, link.to)}
                      className={cn(
                        "display flex items-center justify-between border-b border-border/60 py-4 text-4xl uppercase tracking-wide transition-colors",
                        isActive(link.to) ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                      style={{ animationDelay: `${i * 60}ms` }}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {link.label}
                      <ArrowUpRight className="h-5 w-5 text-muted-foreground" />
                    </NavLink>
                  </SheetClose>
                ))}
              </nav>
              <div className="space-y-4 border-t border-border px-6 py-6">
                <p className="text-sm text-muted-foreground">{studio.address.line1}, {studio.address.city}, {studio.address.province}</p>
                <a href={studio.phoneHref} className="flex items-center gap-2 text-sm font-semibold">
                  <Phone className="h-4 w-4 text-primary" /> {studio.phone}
                </a>
                <SheetClose asChild>
                  <Button asChild className="w-full rounded-none font-semibold uppercase tracking-[0.18em] text-[11px]">
                    <Link to="/artists">Book an Artist</Link>
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Navbar;
